import React from "react";
import { Button } from "./ui/button";
import { useExpenseIframe } from "../hooks/useExpenseIframe";
import { X, ExternalLink, Info, Loader2, ReceiptText } from "lucide-react";

/**
 * Embedded iframe modal that opens the Expenses app "Log expense" form
 * prefilled for a given bill. Listens for a postMessage callback from the
 * Expenses app to persist the created expense id back onto the bill.
 *
 * Expected postMessage shape from expenses.aftfreight.com:
 *   { type: "EXPENSE_LOGGED", expense_id, idempotency_key, status }
 */
export const ExpenseIframeModal = ({ open, link, bill, onClose, onLogged }) => {
  const { iframeLoading, onIframeLoad } = useExpenseIframe({ open, link, bill, onClose, onLogged });

  if (!open || !link) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6"
      data-testid="expense-iframe-modal"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div className="relative z-10 flex h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border bg-background shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-primary">
              <ReceiptText className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-sm font-semibold leading-tight truncate">
                Log expense in Expenses
              </h2>
              <p className="text-xs text-muted-foreground truncate">
                {bill?.issuing_authority || "Toll bill"}
                {bill?.total_amount_due != null ? ` · $${Number(bill.total_amount_due).toFixed(2)}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-testid="expense-open-newtab"
              onClick={() => window.open(link.iframe_url, "_blank", "noopener")}
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              data-testid="expense-modal-close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Fallback note when the prefill handshake wasn't available */}
        {link.mode === "query" && (
          <div
            className="flex items-start gap-2 border-b bg-amber-50 px-4 py-2 text-xs text-amber-800"
            data-testid="expense-fallback-note"
          >
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Prefill link is using query parameters. Fields will populate once the
              Expenses app finishes its prefill setup — the form is fully usable now.
            </span>
          </div>
        )}

        {/* Iframe body */}
        <div className="relative flex-1 bg-muted/30">
          {iframeLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Loading Expenses form…</span>
            </div>
          )}
          <iframe
            title="Log expense"
            src={link.iframe_url}
            className="h-full w-full border-0"
            onLoad={onIframeLoad}
            data-testid="expense-iframe"
          />
        </div>
      </div>
    </div>
  );
};

export default ExpenseIframeModal;
